import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useState, FormEvent } from 'react';
import SchemaOrg from '../../components/base/SchemaOrg';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const OG_IMAGE = 'https://public.readdy.ai/ai/img_res/07aeeb1f747a4138f5cad40a80fef03c.jpg';
const OG_TITLE = 'Sobre a ViaEducacaoCursos | Formação de Professores em Inteligência Artificial';
const OG_DESCRIPTION = 'Conheça a ViaEducacaoCursos: empresa especializada em formação de professores em IA com ChatGPT e Gemini. Nossa missão é transformar a educação com tecnologia, empatia e propósito.';

const sobreSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
      name: 'ViaEducacaoCursos',
      url: siteUrl,
      description:
        'Empresa brasileira especializada em formação de professores em Inteligência Artificial, oferecendo cursos, mentoria e consultoria educacional.',
      foundingDate: '2023',
      inLanguage: 'pt-BR',
      areaServed: 'BR',
      knowsAbout: [
        'Inteligência Artificial na Educação',
        'ChatGPT para Professores',
        'Gemini para Educadores',
        'Documentação Pedagógica com IA',
        'Formação de Professores',
      ],
    },
    {
      '@type': 'AboutPage',
      '@id': `${siteUrl}/sobre`,
      url: `${siteUrl}/sobre`,
      name: 'Sobre a ViaEducacaoCursos',
      description:
        'Conheça a ViaEducacaoCursos: missão, valores e a história por trás da formação de professores em Inteligência Artificial no Brasil.',
      isPartOf: { '@id': siteUrl },
      about: { '@id': `${siteUrl}/#organization` },
      inLanguage: 'pt-BR',
    },
  ],
};

export default function SobrePage() {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    mensagem: ''
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const pageUrl = `${siteUrl}/sobre`;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.email || !formData.mensagem) {
      setStatus('error');
      setMessage('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    setStatus('loading');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('nome', formData.nome);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('telefone', formData.telefone);
      formDataToSend.append('mensagem', formData.mensagem);

      const response = await fetch('https://readdy.ai/api/form/d6b01mdbiueuoqm1sqg0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setStatus('success');
        setMessage('Mensagem enviada com sucesso! Entraremos em contato em breve.');
        setFormData({ nome: '', email: '', telefone: '', mensagem: '' });
      } else {
        setStatus('error');
        setMessage('Erro ao enviar mensagem. Tente novamente.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Erro ao enviar mensagem. Tente novamente.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen">
      {/* Open Graph + Twitter Card */}
      <title>{OG_TITLE}</title>
      <meta name="description" content={OG_DESCRIPTION} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={OG_TITLE} />
      <meta property="og:description" content={OG_DESCRIPTION} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Sobre a ViaEducacaoCursos — Formação de Professores em IA" />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="ViaEducacaoCursos" />
      <meta property="og:locale" content="pt_BR" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={OG_TITLE} />
      <meta name="twitter:description" content={OG_DESCRIPTION} />
      <meta name="twitter:image" content={OG_IMAGE} />
      <meta name="twitter:image:alt" content="Sobre a ViaEducacaoCursos — Formação de Professores em IA" />

      <link rel="canonical" href={pageUrl} />

      <SchemaOrg id="sobre" schema={sobreSchema} />
      <Navbar />
      <main>
        <section className="pt-32 pb-20 bg-gradient-to-b from-purple-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                Sobre a ViaEducacaoCursos
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Transformando a educação através da inteligência artificial e metodologias práticas
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20female%20educator%20rosana%20cristofani%20smiling%20confident%20modern%20office%20setting%20warm%20friendly%20atmosphere%20natural%20lighting%20professional%20portrait%20inspiring%20teacher&width=800&height=1000&seq=about001&orientation=portrait"
                  alt="Rosana Cristófani"
                  className="rounded-3xl shadow-2xl w-full h-auto object-cover"
                />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Nossa História
                </h2>
                <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                  <p>
                    A ViaEducacaoCursos nasceu da experiência real de sala de aula e da compreensão profunda dos desafios que professores enfrentam diariamente.
                  </p>
                  <p>
                    Fundada por educadores que viveram o cansaço da rotina, a pressão por resultados e a sensação de que nunca dá tempo de tudo, encontramos na Inteligência Artificial uma forma de trazer mais leveza e clareza para o que realmente importa: ensinar com intenção.
                  </p>
                  <p>
                    Hoje, compartilhamos esse caminho com outros educadores. Não como solução mágica, mas como um apoio real para quem quer continuar ensinando — com menos peso e mais propósito.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-12 mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
                Nossos Valores
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="w-16 h-16 flex items-center justify-center bg-purple-100 rounded-full mb-6">
                    <i className="ri-heart-line text-3xl text-purple-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Empatia
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Entendemos os desafios reais da sala de aula porque já estivemos lá. Nossa abordagem é sempre humana e acolhedora.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mb-6">
                    <i className="ri-lightbulb-line text-3xl text-blue-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Praticidade
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Tudo que ensinamos é aplicável imediatamente. Nada de teoria sem prática ou ferramentas que não funcionam na realidade.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-6">
                    <i className="ri-compass-line text-3xl text-green-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Propósito
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    A tecnologia é meio, não fim. Nosso propósito é ajudar educadores a ensinar melhor, com mais tempo e menos estresse.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
                Por Que Escolher a ViaEducacaoCursos
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[
                  {
                    icon: 'ri-user-star-line',
                    title: 'Experiência Real',
                    desc: 'Somos professores e gestores com anos de experiência em sala de aula'
                  },
                  {
                    icon: 'ri-tools-line',
                    title: 'Metodologia Testada',
                    desc: 'Tudo que ensinamos foi testado e aprovado em contextos reais de ensino'
                  },
                  {
                    icon: 'ri-customer-service-2-line',
                    title: 'Suporte Contínuo',
                    desc: 'Você não fica sozinho após o curso. Oferecemos suporte via WhatsApp'
                  },
                  {
                    icon: 'ri-community-line',
                    title: 'Comunidade Ativa',
                    desc: 'Faça parte de uma rede de educadores que compartilham experiências'
                  },
                  {
                    icon: 'ri-refresh-line',
                    title: 'Conteúdo Atualizado',
                    desc: 'Mantemos nossos cursos sempre atualizados com as últimas novidades em IA'
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: 'Garantia Total',
                    desc: '15 dias de garantia incondicional em todos os nossos cursos'
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-lg">
                    <div className="w-14 h-14 flex items-center justify-center bg-purple-100 rounded-xl flex-shrink-0">
                      <i className={`${item.icon} text-2xl text-purple-600`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-3xl shadow-2xl p-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
                Entre em Contato
              </h2>
              <div className="max-w-2xl mx-auto">
                <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contato-form">
                  <div>
                    <label htmlFor="nome" className="block text-sm font-semibold text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-purple-500 transition-colors text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-purple-500 transition-colors text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="telefone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Telefone
                    </label>
                    <input
                      type="tel"
                      id="telefone"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-purple-500 transition-colors text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="mensagem" className="block text-sm font-semibold text-gray-700 mb-2">
                      Mensagem *
                    </label>
                    <textarea
                      id="mensagem"
                      name="mensagem"
                      value={formData.mensagem}
                      onChange={handleChange}
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-purple-500 transition-colors resize-none text-sm"
                      required
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">
                      Máximo de 500 caracteres
                    </p>
                  </div>
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-full hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {status === 'loading' ? 'Enviando...' : 'Enviar Mensagem'}
                  </button>
                  {message && (
                    <p className={`text-center text-sm ${status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                      {message}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}